/**
 * Drop-in replacement for the `xlsx` (SheetJS) library using ExcelJS.
 * Provides the same API surface used across this codebase.
 * Replaces the vulnerable `xlsx` package (no upstream fix available).
 */
import ExcelJS from 'exceljs';

export interface Worksheet {
  data: any[][];
  name: string;
  colWidths?: number[];
  '!cols'?: { wch: number }[];
  [key: string]: any;
}

export interface Workbook {
  sheets: Worksheet[];
}

// ─── book_new ────────────────────────────────────────────────────────────────
function book_new(): Workbook {
  return { sheets: [] };
}

// ─── json_to_sheet ───────────────────────────────────────────────────────────
function json_to_sheet(data: Record<string, any>[]): Worksheet {
  if (!data || data.length === 0) return { data: [], name: '' };
  const headers = Object.keys(data[0]);
  const rows = data.map(row => headers.map(h => row[h] ?? ''));
  return { data: [headers, ...rows], name: '' };
}

// ─── aoa_to_sheet ────────────────────────────────────────────────────────────
function aoa_to_sheet(data: any[][]): Worksheet {
  return { data, name: '' };
}

// ─── book_append_sheet ───────────────────────────────────────────────────────
function book_append_sheet(wb: Workbook, ws: Worksheet, name: string): void {
  wb.sheets.push({ ...ws, name });
}

// ─── sheet_to_json ───────────────────────────────────────────────────────────
function sheet_to_json(ws: Worksheet, opts?: { header?: number | 'A' | 1 }): any[] {
  const { data } = ws;
  if (!data || data.length === 0) return [];

  if (opts?.header === 1) {
    // Return array of arrays
    return data;
  }

  // Return array of objects using first row as headers
  const headers = data[0] as string[];
  return data.slice(1).map(row => {
    const obj: Record<string, any> = {};
    headers.forEach((h, i) => { obj[h] = row[i]; });
    return obj;
  });
}

// ─── read (parse uploaded Excel file) ────────────────────────────────────────
function read(data: Uint8Array | ArrayBuffer, _opts?: any): Workbook {
  // ExcelJS requires async reading; we return a sync-compatible shim.
  // The actual parsing happens in the async helpers below (readAsync).
  // This stub exists so code that calls XLSX.read() synchronously still compiles.
  // Use xlsxCompat.readAsync() in async contexts.
  throw new Error(
    'xlsxCompat.read() is not supported. Use xlsxCompat.readAsync(arrayBuffer) instead.'
  );
}

// ─── readAsync (replaces XLSX.read for async callers) ────────────────────────
async function readAsync(arrayBuffer: ArrayBuffer): Promise<Workbook> {
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(arrayBuffer);

  const sheets: Worksheet[] = [];
  wb.eachSheet(sheet => {
    const data: any[][] = [];
    sheet.eachRow(row => {
      data.push((row.values as any[]).slice(1)); // ExcelJS rows are 1-indexed
    });
    sheets.push({ name: sheet.name, data });
  });

  return { sheets };
}

// ─── write (serialize workbook → Uint8Array) ─────────────────────────────────
async function writeAsync(wb: Workbook, _opts?: any): Promise<Uint8Array> {
  const excelWb = new ExcelJS.Workbook();

  for (const sheet of wb.sheets) {
    const ws = excelWb.addWorksheet(sheet.name || 'Sheet1');

    const colDefs = sheet['!cols'] || (sheet.colWidths ? sheet.colWidths.map(w => ({ wch: w })) : null);
    if (colDefs) {
      ws.columns = colDefs.map((col: { wch: number }, i: number) => ({
        key: String(i),
        width: col.wch
      }));
    }

    for (const row of sheet.data) {
      ws.addRow(row);
    }
  }

  const buffer = await excelWb.xlsx.writeBuffer();
  return new Uint8Array(buffer as ArrayBuffer);
}

// ─── writeFile (download workbook as file) ────────────────────────────────────
async function writeFile(wb: Workbook, filename: string, _opts?: any): Promise<void> {
  const buffer = await writeAsync(wb);
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── write (sync-like, returns array — wraps async internally) ────────────────
// Note: this is async under the hood; callers must await xlsxCompat.write()
async function write(wb: Workbook, _opts?: any): Promise<Uint8Array> {
  return writeAsync(wb);
}

export const utils = { book_new, json_to_sheet, aoa_to_sheet, book_append_sheet, sheet_to_json };
export { book_new, json_to_sheet, aoa_to_sheet, book_append_sheet, sheet_to_json, read, readAsync, write, writeAsync, writeFile };
